import { BrowserRouter } from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";
import {
  FeatureFlagsContextProvider,
  useInitializeNavigate,
} from "@b2bportal/core-utilities";
import I18nProvider, { getLang, useI18nContext } from "@hopper-b2b/i18n";
import {
  StylesProvider,
  ThemeProvider,
  createGenerateClassName,
  createTheme,
  jssPreset,
} from "@material-ui/core";
import { create } from "jss";
import rtl from "jss-rtl";
import {
  Env,
  type MUIThemeDefinition,
  type PortalConfig,
} from "../../../types";

export interface RootProps extends React.PropsWithChildren {
  config: PortalConfig;

  /**
   * Only for testing purposes. This overrides the `defaultLng` parameter
   * passed to the `I18nProvider`. Without this prop, the default language is
   * determined by what's in the `localStorage` and `navigator.language`
   * values, which would prevent us from being able to directly specify
   * languages for tests without resorting to hacks like writing to
   * `localStorage` before mounting.
   */
  testLocale?: string;
}

export const Root = ({ config, children, testLocale }: RootProps) => {
  const defaultLocale = testLocale ?? getLang(config.branding.default);
  // Assume we are in production if we aren't told otherwise
  const env = config?.bootConfig?.env ?? Env.production;
  const isDebugEnv = env !== Env.production;

  return (
    <BrowserRouter
      {...(config.routerBasename && { basename: config.routerBasename })}
    >
      <CompatRouter>
        <I18nProvider
          enableDebugHook={isDebugEnv}
          defaultLng={defaultLocale}
          branding={config.branding}
          tenantTranslation={config.translations}
          providedTranslations={config.providedTranslations}
          useGeneratedTranslations={config.useGeneratedTranslations}
        >
          <FeatureFlagsContextProvider featureFlags={config.featureFlags}>
            <MuiProviders muiTheme={config.theme.muiTheme}>
              {children}
            </MuiProviders>
          </FeatureFlagsContextProvider>
        </I18nProvider>
        <NavigateGlobalHelper />
      </CompatRouter>
    </BrowserRouter>
  );
};

interface MuiProviderProps extends React.PropsWithChildren {
  muiTheme?: MUIThemeDefinition;
}

const MuiProviders = ({ muiTheme, children }: MuiProviderProps) => {
  const theme = createTheme(muiTheme);
  const generateClassName = createGenerateClassName({
    productionPrefix: "core-portal",
    seed: "core-portal",
  });

  // Add right to left (RTL) language support to MUI theme
  const { dir, language } = useI18nContext();
  theme.direction = dir(language);

  // Pass custom jss object for we may need to add the `rtl` plugin
  // See https://v4.mui.com/guides/right-to-left/
  const jss = create({ plugins: jssPreset().plugins });
  if (theme.direction === "rtl") {
    jss.use(rtl());
  }

  return (
    <StylesProvider generateClassName={generateClassName} jss={jss}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </StylesProvider>
  );
};

const NavigateGlobalHelper = () => {
  useInitializeNavigate();
  return null;
};
